import {
  FINDING_STATUS_LABELS,
  type HandoverPhoto,
  type HandoverReinspection,
  REINSPECTION_RESULT_LABELS,
  type ReinspectionResult,
} from '@sivan/shared';
import { useId, useState } from 'react';
import { Value } from '../master-data/Value';
import { ResultSegment } from '../ui/ResultSegment';
import { PendingPhotos } from './PendingPhotos';
import { PhotoField } from './PhotoField';
import { PhotoStrip, PhotoViewer } from './PhotoViewer';
import {
  type DraftReinspection,
  type ReinspectionErrors,
  formatDate,
  today,
  validateReinspection,
} from './handover-draft';
import { REINSPECTION_SEGMENTS } from './results';

// "ליקויים לבדיקה חוזרת" (H-Next): in the final handover, the initial handover's open findings (and
// those already reinspected here), each as it was recorded there (room, component, description,
// photos, contractor), with the reinspection result, date, notes and new photos. The initial
// finding is shown, never changed; marking a checklist item OK does not mark a finding repaired.

export const reinspectionElementId = (findingId: string) => `reinspection-${findingId}`;
export const REINSPECTION_SECTION_ID = 'reinspection';

const RESULTS: readonly ReinspectionResult[] = ['repaired', 'still_defective', 'pending'];

export function ReinspectionPanel({
  reinspection,
  draft,
  readOnly,
  dirty,
  linking,
  showErrors,
  initialPhotoUrl,
  photoUrl,
  savedPhotoIds,
  onResult,
  onEdit,
  onLink,
}: {
  readonly reinspection: HandoverReinspection;
  readonly draft: Readonly<Record<string, DraftReinspection>>;
  readonly readOnly: boolean;
  readonly dirty: boolean;
  readonly linking: boolean;
  readonly showErrors: boolean;
  /** Photos of the initial handover. */
  readonly initialPhotoUrl: (photo: HandoverPhoto) => string;
  /** New photos (of this handover). */
  readonly photoUrl: (photo: HandoverPhoto) => string;
  /** Photos attached on the server (others wait for the next save). */
  readonly savedPhotoIds: ReadonlySet<string>;
  readonly onResult: (findingId: string, result: ReinspectionResult) => void;
  readonly onEdit: (findingId: string) => void;
  readonly onLink: () => void;
}) {
  const titleId = useId();
  const [viewing, setViewing] = useState<{
    photos: readonly HandoverPhoto[];
    index: number;
    initial: boolean;
  } | null>(null);
  const items = reinspection.items.map((r) => ({
    ...r,
    current: draft[r.finding.id] ?? {
      result: r.result,
      inspectedOn: r.inspectedOn ?? '',
      note: r.note ?? '',
      photos: r.photos,
    },
  }));
  const pending = items.filter((r) => r.current.result === 'pending').length;
  const still = items.filter((r) => r.current.result === 'still_defective').length;

  return (
    <section
      id={REINSPECTION_SECTION_ID}
      className="editor-section reinspection"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <div className="inspection__head">
        <h2 id={titleId} className="section-title">
          ליקויים לבדיקה חוזרת <span className="count">({items.length})</span>
        </h2>
        {reinspection.initial !== null && (
          <p className="inspection__summary">
            מהמסירה הראשונית <Value text={reinspection.initial.protocolNumber} />
            {reinspection.initial.handoverDate !== null &&
              ` (${formatDate(reinspection.initial.handoverDate)})`}
            {items.length > 0 &&
              ` · ${pending === 1 ? 'אחד ממתין' : `${String(pending)} ממתינים`} לבדיקה חוזרת`}
            {still > 0 && ` · ${String(still)} עדיין לא תקין`}
          </p>
        )}
      </div>

      {reinspection.initial === null ? (
        reinspection.linkable === null ? (
          <p className="hint">
            אין עדיין מסירה ראשונית לדירה, ולכן אין ליקויים לבדיקה חוזרת. אם תיפתח מסירה ראשונית,
            ניתן יהיה לקשר אליה מכאן.
          </p>
        ) : (
          <div className="notice">
            <p>
              מסירה זו אינה מקושרת למסירה הראשונית{' '}
              <Value text={reinspection.linkable.protocolNumber} />. לאחר הקישור יוצגו כאן הליקויים
              הפתוחים שלה לבדיקה חוזרת. הקישור נשמר ואינו משתנה.
            </p>
            {!readOnly && (
              <>
                <button
                  type="button"
                  className="button button--primary"
                  disabled={dirty || linking}
                  onClick={onLink}
                >
                  {linking
                    ? 'מקשר…'
                    : `קישור למסירה הראשונית ${reinspection.linkable.protocolNumber}`}
                </button>
                {dirty && <p className="hint">יש לשמור את הטיוטה לפני הקישור.</p>}
              </>
            )}
          </div>
        )
      ) : items.length === 0 ? (
        <p className="empty">אין ליקויים פתוחים מהמסירה הראשונית.</p>
      ) : (
        <>
          <p className="hint">
            הליקויים מוצגים כפי שנרשמו במסירה הראשונית ואינם משתנים. סימון פריט כתקין ברשימת הבדיקה
            אינו מסמן ליקוי כמתוקן: יש לרשום כאן את תוצאת הבדיקה החוזרת.
          </p>
          <ul className="reinspection-list">
            {items.map(({ finding, current }) => {
              const errors: ReinspectionErrors = showErrors ? validateReinspection(current) : {};
              return (
                <li
                  key={finding.id}
                  id={reinspectionElementId(finding.id)}
                  tabIndex={-1}
                  className={`reinspection-item reinspection-item--${current.result}`}
                  aria-label={`${finding.area} · ${finding.component}`}
                >
                  <div className="reinspection-item__original">
                    <p className="finding__component">
                      {finding.area} · {finding.component}
                    </p>
                    <p className="finding__description">{finding.description}</p>
                    <p className="finding__meta">
                      <span>
                        נרשם <Value text={formatDate(finding.inspectedOn)} />
                      </span>
                      {finding.workCategory !== null && <span>תחום: {finding.workCategory}</span>}
                      {finding.contractor !== null && <span>קבלן / ספק: {finding.contractor}</span>}
                      {finding.status !== 'open' && (
                        <span className={`chip chip--finding-${finding.status}`}>
                          במסירה הראשונית: {FINDING_STATUS_LABELS[finding.status]}
                        </span>
                      )}
                    </p>
                    <PhotoStrip
                      photos={finding.photos}
                      urlOf={initialPhotoUrl}
                      label={`תמונות מהמסירה הראשונית: ${finding.component}`}
                      onOpen={(index) => {
                        setViewing({ photos: finding.photos, index, initial: true });
                      }}
                    />
                  </div>
                  <div className="reinspection-item__result">
                    <ResultSegment
                      label={`תוצאת בדיקה חוזרת: ${finding.component}`}
                      segments={REINSPECTION_SEGMENTS}
                      value={current.result}
                      disabled={readOnly}
                      onChoose={(result) => {
                        onResult(finding.id, result);
                      }}
                    />
                    {current.result !== 'pending' && (
                      <p className="finding__meta">
                        <span>
                          נבדק מחדש <Value text={formatDate(current.inspectedOn)} />
                        </span>
                      </p>
                    )}
                    {current.note.trim() !== '' && (
                      <p className="reinspection-item__note">{current.note}</p>
                    )}
                    {errors.inspectedOn !== undefined && (
                      <p className="field__error">{errors.inspectedOn}</p>
                    )}
                    <PendingPhotos photos={current.photos} saved={savedPhotoIds} />
                    <PhotoStrip
                      photos={current.photos}
                      urlOf={photoUrl}
                      label={`תמונות מהבדיקה החוזרת: ${finding.component}`}
                      onOpen={(index) => {
                        setViewing({ photos: current.photos, index, initial: false });
                      }}
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        className="button button--small"
                        aria-label={`פרטי בדיקה חוזרת ותמונות: ${finding.component}`}
                        onClick={() => {
                          onEdit(finding.id);
                        }}
                      >
                        פרטים ותמונות
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <PhotoViewer
        photos={viewing?.photos ?? []}
        index={viewing?.index ?? null}
        urlOf={viewing?.initial === true ? initialPhotoUrl : photoUrl}
        onIndex={(index) => {
          setViewing((v) => (v === null ? null : { ...v, index }));
        }}
        onClose={() => {
          setViewing(null);
        }}
      />
    </section>
  );
}

/** The reinspection of one finding in a dialog: result, date, notes and new photos. */
export function ReinspectionForm({
  title,
  initial,
  uploadPath,
  photoUrl,
  onUploaded,
  onSubmit,
  onCancel,
}: {
  readonly title: string;
  readonly initial: DraftReinspection;
  readonly uploadPath: string;
  readonly photoUrl: (photo: HandoverPhoto) => string;
  readonly onUploaded: (photo: HandoverPhoto) => void;
  readonly onSubmit: (value: DraftReinspection) => void;
  readonly onCancel: () => void;
}) {
  const [value, setValue] = useState<DraftReinspection>(() => ({
    ...initial,
    inspectedOn: initial.inspectedOn === '' ? today() : initial.inspectedOn,
  }));
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const id = useId();
  const errors = showErrors ? validateReinspection(value) : {};
  return (
    <form
      className="reinspection-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (Object.keys(validateReinspection(value)).length > 0) {
          setShowErrors(true);
          return;
        }
        onSubmit({ ...value, inspectedOn: value.result === 'pending' ? '' : value.inspectedOn });
      }}
    >
      <p className="hint">{title}</p>
      <fieldset className="resolve-form__choices">
        <legend>תוצאת הבדיקה החוזרת</legend>
        {RESULTS.map((result) => (
          <label key={result} className="choice">
            <input
              type="radio"
              name={`${id}-result`}
              checked={value.result === result}
              onChange={() => {
                setValue((v) => ({ ...v, result }));
              }}
            />
            {REINSPECTION_RESULT_LABELS[result]}
          </label>
        ))}
      </fieldset>
      {value.result !== 'pending' && (
        <label className="field">
          <span>תאריך הבדיקה החוזרת</span>
          <input
            type="date"
            value={value.inspectedOn}
            aria-invalid={errors.inspectedOn !== undefined}
            onChange={(e) => {
              const inspectedOn = e.target.value;
              setValue((v) => ({ ...v, inspectedOn }));
            }}
          />
          {errors.inspectedOn !== undefined && <p className="field__error">{errors.inspectedOn}</p>}
        </label>
      )}
      <label className="field">
        <span>{value.result === 'still_defective' ? 'מה עדיין לא תקין' : 'הערות'}</span>
        <textarea
          rows={3}
          value={value.note}
          aria-invalid={errors.note !== undefined}
          onChange={(e) => {
            const note = e.target.value;
            setValue((v) => ({ ...v, note }));
          }}
        />
        {errors.note !== undefined && <p className="field__error">{errors.note}</p>}
      </label>
      <PhotoField
        photos={value.photos}
        uploadPath={uploadPath}
        urlOf={photoUrl}
        onBusy={setBusy}
        onOpen={setViewing}
        onAdd={(photo) => {
          onUploaded(photo);
          setValue((v) => ({ ...v, photos: [...v.photos, photo] }));
        }}
        onRemove={(photo) => {
          setValue((v) => ({ ...v, photos: v.photos.filter((p) => p.id !== photo.id) }));
        }}
      />
      <p className="hint">הליקוי המקורי ותמונותיו נשמרים כפי שהם במסירה הראשונית.</p>
      <div className="form-actions">
        <button type="submit" className="button button--primary" disabled={busy}>
          עדכון הבדיקה החוזרת
        </button>
        <button type="button" className="button" onClick={onCancel}>
          ביטול
        </button>
      </div>
      <PhotoViewer
        photos={value.photos}
        index={viewing}
        urlOf={photoUrl}
        onIndex={setViewing}
        onClose={() => {
          setViewing(null);
        }}
      />
    </form>
  );
}
